import { router } from "@/main";
import { GET } from "@/state/api";
import { POST, queryClient, server } from "@/state/server";
import { AdminFotografoZod } from "@/state/types";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { createFileRoute } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { z } from "zod";

export const Route = createFileRoute("/admin/config/personal")({
  component: RouteComponent,
  loader: async () => (await GET("/admin/fotografos")).fotografos,
});

const permisos = ["fotografo", "cajero", "impresor", "contador", "admin"]; // TODO: deberia ir en algun lugar centralizado

type Fotografo = z.infer<typeof AdminFotografoZod>;

function RouteComponent() {
  const fotografos = Route.useLoaderData();

  return <Fotografos fotografosOrig={fotografos} />;
}

function Fotografos({
  fotografosOrig,
}: {
  fotografosOrig: Record<string, Fotografo>;
}) {
  const [fotografos, setFotografos] = useState(
    Object.values(fotografosOrig).sort(
      (a, b) =>
        (a.permisos.length > 0 ? 0 : 1) - (b.permisos.length > 0 ? 0 : 1) ||
        a.nombre.localeCompare(b.nombre)
    )
  );
  const [todos, setTodos] = useState(false);

  return (
    <>
      <h4>Fotografos</h4>
      {fotografos.map(
        (f, i) =>
          (todos || !f.id || f.permisos[0]) && (
            <Fotografo
              key={i}
              fotografo={f}
              onChange={(f) =>
                setFotografos([
                  ...fotografos.slice(0, i),
                  f,
                  ...fotografos.slice(i + 1),
                ])
              }
            />
          )
      )}
      {!todos && (
        <li>
          <span onClick={() => setTodos(true)}>Ver todos</span>
        </li>
      )}
      <li>
        <FontAwesomeIcon
          icon={faPlus}
          onClick={() =>
            setFotografos([
              ...fotografos,
              {
                id: 0,
                nombre: "",
                apellido: "",
                email: "",
                permisos: [],
                codcorto: "",
              },
            ])
          }
        />
      </li>
      <div>
        <button
          onClick={() =>
            POST("/admin/fotografos", {
              fotografos: fotografos.filter((f) => f != fotografosOrig[f.id]),
            }).then((x) => {
              if (!("error" in x)) {
                queryClient.invalidateQueries({
                  queryKey: ["/admin/fotografos"],
                });
                router.navigate({ to: "/admin/config" });
              } else {
                window.scrollTo({ top: 0 });
              }
            })
          }
        >
          Enviar
        </button>
      </div>
    </>
  );
}

function Fotografo({
  fotografo,
  onChange,
}: {
  fotografo: Fotografo;
  onChange: (f: Fotografo) => unknown;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  const [forceRefresh, setForceRefresh] = useState(true);

  const errorFoto =
    (fotografo.foto?.length || 0) > 50000
      ? "Foto >50kb"
      : !!imgRef.current?.naturalHeight &&
        (![266, 267].includes(imgRef.current.naturalHeight) ||
          imgRef.current.naturalWidth != 200) &&
        `Dimensiones incorrectas ${imgRef.current.naturalWidth}x${imgRef.current?.naturalHeight}`;

  return (
    <div style={{ clear: "both" }}>
      <input
        type="file"
        ref={fileRef}
        style={{ display: "none" }}
        onChange={async (e) =>
          // OJO, si demora en cargar (no deberia, es local) resetea los otros datos del fotografo
          onChange({ ...fotografo, foto: await getContents(e.target) })
        }
        accept=".jpg,.jpeg"
      />
      <div
        style={{ float: "left", width: 100 }}
        className={errorFoto ? "alert alert-danger" : ""}
      >
        <img
          ref={imgRef}
          src={
            fotografo.foto
              ? `data:image/jpeg;base64,${fotografo.foto}`
              : `${server}/fotografo/${fotografo.id}.jpg`
          }
          style={{ width: "90%" }}
          onClick={() => fileRef.current?.click()}
          onLoad={() => setForceRefresh(!forceRefresh)}
        />
        {errorFoto && (
          <div>
            {errorFoto}{" "}
            <a
              href="#/admin/config/editFoto"
              target="_blank"
              rel="noopener noreferrer"
            >
              Editor
            </a>
          </div>
        )}
      </div>
      <div
        style={{ display: "inline-block", marginLeft: 15, marginBottom: 10 }}
      >
        <input
          value={fotografo.email}
          onChange={(e) => onChange({ ...fotografo, email: e.target.value })}
          placeholder="Email"
        />
        <input
          value={fotografo.nombre}
          onChange={(e) => onChange({ ...fotografo, nombre: e.target.value })}
          placeholder="Nombre"
        />
        <input
          value={fotografo.apellido}
          onChange={(e) => onChange({ ...fotografo, apellido: e.target.value })}
          placeholder="Apellido"
        />
        <input
          value={fotografo.codcorto}
          onChange={(e) => onChange({ ...fotografo, codcorto: e.target.value })}
          placeholder="Codigo corto"
        />
        <input
          type="password"
          onChange={(e) => onChange({ ...fotografo, password: e.target.value })}
          placeholder="Contraseña"
        />
        <ul>
          {permisos.map((p) => (
            <li>
              <input
                type="checkbox"
                checked={fotografo.permisos.includes(p)}
                onChange={(e) =>
                  onChange({
                    ...fotografo,
                    permisos: e.target.checked
                      ? [...fotografo.permisos, p]
                      : fotografo.permisos.filter((x) => x != p),
                  })
                }
              />
              {p}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function getContents(file: HTMLInputElement): Promise<string> {
  return new Promise((resolve, reject) => {
    if (file.files?.[0]) {
      const reader = new FileReader();
      const jpegPrefix = "data:image/jpeg;base64,";

      reader.onload = (e) => {
        const t = (e.target?.result as string) || "";
        if (t.startsWith(jpegPrefix)) {
          resolve(t.substring(jpegPrefix.length));
        } else {
          reject(new Error("Only JPG images are allowed."));
        }
      };

      reader.onerror = () => {
        reject(new Error("Failed to read the file."));
      };

      reader.readAsDataURL(file.files[0]);
    } else {
      reject(new Error("No file selected."));
    }
  });
}
